// Chatbot Response Parser Utility
// Handles parsing and formatting of different types of chatbot responses

export interface ParsedTableRow {
  [key: string]: string;
}

export interface ParsedTable {
  headers: string[];
  rows: ParsedTableRow[];
}

export interface ResponseSections {
  title?: string;
  table?: ParsedTable;
  insights?: string;
  recommendations?: string;
  source?: string;
  defaultText?: string;
}

export type ResponseType = 'table' | 'comparison' | 'salary' | 'default';

// Detect response type based on content patterns
export function detectResponseType(response: string): ResponseType {
  // Check for table format (markdown tables with | separators)
  if (response.includes('|') && response.includes('---')) {
    if (response.includes('Occupation Comparison:') || response.includes('**Occupation Comparison:**')) {
      return 'comparison';
    }
    if (response.includes('Highest-Paying Jobs') || response.includes('Lowest Paying Jobs') || response.includes('Salary')) {
      return 'salary';
    }
    return 'table';
  }
  
  // Check for tab-separated tables or space-separated tables
  if ((response.includes('\t') || response.includes('  ')) && 
      (response.includes('Metric') || response.includes('Rank') || response.includes('Job Title'))) {
    if (response.includes('Occupation Comparison:')) {
      return 'comparison';
    }
    if (response.includes('Highest-Paying Jobs') || response.includes('Lowest Paying Jobs')) {
      return 'salary';
    }
    return 'table';
  }
  
  return 'default';
}

// Extract title from response
export function extractTitle(response: string): string {
  const titleMatch = response.match(/\*\*(.*?)\*\*/);
  return titleMatch ? titleMatch[1].replace(/\*/g, '').trim() : 'Response';
}

// Parse markdown table from response
export function parseTable(response: string): ParsedTable | null {
  const lines = response.split('\n');
  const tableLines = lines.filter(line => 
    line.includes('|') && !line.includes('---') && line.trim()
  );
  
  if (tableLines.length === 0) {
    return null;
  }
  
  // Parse headers (first line)
  const headerLine = tableLines[0];
  const headers = headerLine.split('|')
    .map(cell => cell.trim())
    .filter(cell => cell.length > 0)
    .map(header => header.replace(/\*\*/g, '').replace(/\*/g, '').trim()); // Clean markdown formatting
  
  // Parse data rows
  const rows = tableLines.slice(1).map((line, rowIndex) => {
    const cells = line.split('|')
      .map(cell => cell.trim())
      .filter(cell => cell.length > 0); // Remove empty cells
    
    const row: ParsedTableRow = {};
    headers.forEach((header, index) => {
      // Make sure we don't go out of bounds
      const cellValue = cells[index] || '';
      // Clean up markdown formatting (remove ** and *)
      const cleanValue = cellValue.replace(/\*\*/g, '').replace(/\*/g, '').trim();
      row[header] = cleanValue;
    });
    return row;
  });
  
  return { headers, rows };
}

// Parse tab-separated table from response (alternative format)
export function parseTabTable(response: string): ParsedTable | null {
  const lines = response.split('\n');
  const tableLines = lines.filter(line => 
    line.includes('\t') && line.trim() && !line.includes('Source:')
  );
  
  if (tableLines.length === 0) {
    return null;
  }
  
  // Find the header line (usually contains "Metric", "Rank", etc.)
  const headerLineIndex = tableLines.findIndex(line => 
    line.toLowerCase().includes('metric') || 
    line.toLowerCase().includes('rank') ||
    line.toLowerCase().includes('job title')
  );
  
  if (headerLineIndex === -1) {
    return null;
  }
  
  const headerLine = tableLines[headerLineIndex];
  const headers = headerLine.split('\t')
    .map(cell => cell.trim())
    .filter(cell => cell.length > 0);
  
  // Parse data rows
  const rows = tableLines.slice(headerLineIndex + 1).map((line, rowIndex) => {
    const cells = line.split('\t')
      .map(cell => cell.trim())
      .filter(cell => cell.length > 0); // Remove empty cells
    
    const row: ParsedTableRow = {};
    headers.forEach((header, index) => {
      row[header] = cells[index] || '';
    });
    return row;
  }).filter(row => Object.values(row).some(value => value.length > 0));
  
  return { headers, rows };
}

// Parse any table format (tries multiple parsing strategies)
export function parseAnyTable(response: string): ParsedTable | null {
  // Try markdown table first
  let table = parseTable(response);
  if (table) {
    return table;
  }
  
  // Try tab-separated table
  table = parseTabTable(response);
  if (table) {
    return table;
  }
  
  // Try space-separated table (fallback)
  const lines = response.split('\n');
  const tableLines = lines.filter(line => 
    line.trim() && !line.includes('Source:') && !line.includes('**')
  );
  
  if (tableLines.length >= 2) {
    // Look for a header line
    const headerLineIndex = tableLines.findIndex(line => 
      line.toLowerCase().includes('metric') || 
      line.toLowerCase().includes('rank') ||
      line.toLowerCase().includes('job title')
    );
    
    if (headerLineIndex !== -1 && headerLineIndex < tableLines.length - 1) {
      const headerLine = tableLines[headerLineIndex];
      // Split by multiple spaces
      const headers = headerLine.split(/\s{2,}/)
        .map(cell => cell.trim())
        .filter(cell => cell.length > 0);
      
      if (headers.length > 1) {
        const rows = tableLines.slice(headerLineIndex + 1).map(line => {
          const cells = line.split(/\s{2,}/)
            .map(cell => cell.trim())
            .filter(cell => cell.length > 0); // Remove empty cells
          const row: ParsedTableRow = {};
          headers.forEach((header, index) => {
            row[header] = cells[index] || '';
          });
          return row;
        }).filter(row => Object.values(row).some(value => value.length > 0));
        
        return { headers, rows };
      }
    }
  }
  
  // Try to parse the specific format from your JSON response
  // This handles the exact markdown format for both salary and comparison tables
  const markdownTableMatch = response.match(/\|([^|]+)\|([^|]+)\|([^|]+)(?:\|([^|]+))?\|/g);
  if (markdownTableMatch && markdownTableMatch.length >= 2) {
    // First line should be headers
    const headerLine = markdownTableMatch[0];
    const headers = headerLine.split('|')
      .map(cell => cell.trim())
      .filter(cell => cell.length > 0);
    
    // Skip the separator line and parse data rows
    const dataLines = markdownTableMatch.slice(2); // Skip header and separator
    const rows: ParsedTableRow[] = [];
    
    dataLines.forEach((line, index) => {
      const cells = line.split('|')
        .map(cell => cell.trim())
        .filter(cell => cell.length > 0);
      
      if (cells.length === headers.length) {
        const row: ParsedTableRow = {};
        headers.forEach((header, cellIndex) => {
          row[header] = cells[cellIndex] || '';
        });
        rows.push(row);
      }
    });
    
    if (rows.length > 0) {
      return { headers, rows };
    }
  }
  
  return null;
}

// Extract insights section from comparison responses
export function extractInsights(response: string): string {
  const insightsMatch = response.match(/\*\*Key Insights:\*\*(.*?)(?=\*\*|$)/);
  return insightsMatch ? insightsMatch[1].trim() : '';
}

// Extract recommendations section
export function extractRecommendations(response: string): string {
  const recMatch = response.match(/\*\*Recommendation:\*\*(.*?)(?=Source:|$)/);
  return recMatch ? recMatch[1].trim() : '';
}

// Extract source information
export function extractSource(response: string): string {
  const sourceMatch = response.match(/Source:\s*(.*?)(?:\n|$)/);
  return sourceMatch ? sourceMatch[1].trim() : 'Forward Sumner database';
}

// Parse complete response into structured sections
export function parseResponse(response: string): ResponseSections {
  const responseType = detectResponseType(response);
  const title = extractTitle(response);
  
  const sections: ResponseSections = { title };
  
  if (responseType === 'table' || responseType === 'comparison' || responseType === 'salary') {
    // Use the comprehensive table parser
    const table = parseAnyTable(response);
    sections.table = table || undefined;
    sections.insights = extractInsights(response);
    sections.recommendations = extractRecommendations(response);
  } else {
    sections.defaultText = response;
  }
  
  sections.source = extractSource(response);
  
  return sections;
}

// Format currency values for display
export function formatCurrency(value: string): string {
  // Remove any existing currency symbols and clean up
  const cleaned = value.replace(/[$,\s]/g, '');
  const numValue = parseFloat(cleaned);
  
  if (isNaN(numValue)) {
    return value; // Return original if not a number
  }
  
  // Handle incomplete salary values from the API
  // The API returns values like 484, 368, 366 which are clearly incomplete
  let formattedValue = numValue;
  
  // If the number is very small (like 484), it's likely missing thousands
  // For medical professions, salaries are typically in the hundreds of thousands
  if (numValue < 1000 && numValue > 100) {
    formattedValue = numValue * 1000;
  }
  // If the number is very small (like 29), it might be missing more zeros
  else if (numValue < 100 && numValue > 10) {
    formattedValue = numValue * 10000; // 29 -> 290,000
  }
  
  // Format as currency
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(formattedValue);
}

// Format percentage values
export function formatPercentage(value: string): string {
  const cleaned = value.replace(/[%,\s]/g, '');
  const numValue = parseFloat(cleaned);
  
  if (isNaN(numValue)) {
    return value;
  }
  
  return `${numValue}%`;
}

// Format SOC codes properly
export function formatSOCCode(value: string): string {
  // If it already looks like a proper SOC code (XX-XXXX format), return as is
  if (value.match(/^\d{2}-\d{4}$/)) {
    return value;
  }
  
  // If it looks like a malformed SOC code (e.g., "$29"), try to extract the number
  const socMatch = value.match(/\$?(\d{2})/);
  if (socMatch) {
    const baseCode = socMatch[1];
    // This is a simplified SOC code - in real implementation, you'd want to look up the full code
    return `${baseCode}-XXXX`;
  }
  
  return value;
}

// Get appropriate CSS class for response type
export function getResponseTypeClass(responseType: ResponseType): string {
  switch (responseType) {
    case 'table':
      return 'chatbot-table-response';
    case 'comparison':
      return 'chatbot-comparison-response';
    case 'salary':
      return 'chatbot-salary-response';
    default:
      return 'chatbot-default-response';
  }
}

// Check if a cell contains numeric data that should be formatted
export function shouldFormatAsCurrency(header: string, value: string): boolean {
  const currencyHeaders = ['salary', 'wage', 'pay', 'income', 'earnings'];
  return currencyHeaders.some(h => header.toLowerCase().includes(h)) && 
         /[\d,]+/.test(value);
}

export function shouldFormatAsPercentage(header: string, value: string): boolean {
  const percentageHeaders = ['growth', 'change', 'rate', 'percent'];
  return percentageHeaders.some(h => header.toLowerCase().includes(h)) && 
         /[\d.]+/.test(value);
}

// Check if this is a salary difference column that needs special handling
export function shouldFormatAsSalaryDifference(header: string, value: string): boolean {
  return header.toLowerCase().includes('difference') && 
         value.toLowerCase().includes('higher');
}

// ====== NEW PARSERS FOR ENHANCED RESPONSE TYPES ======

export interface JobPosting {
  title: string;
  company: string;
  location: string;
  applyUrl?: string;
}

export interface JobPostingsResponse {
  title: string;
  jobs: JobPosting[];
}

export interface BusinessListing {
  name: string;
  location: string;
  industry?: string;
  phone?: string;
  mapUrl?: string;
  naics?: string;
}

export interface BusinessListingsResponse {
  title: string;
  businesses: BusinessListing[];
}

export interface WageComparisonResponse {
  title: string;
  subtitle?: string;
  occupation: string;
  socCode?: string;
  sumnerWage?: number;
  stateWage?: number;
  nationalWage?: number;
  insights: string[];
  note?: string;
}

export interface CareerPathStep {
  stepNumber: number;
  title: string;
  education?: string;
  requirements?: string;
  salary?: string;
  time?: string;
}

// Parse Job Postings (💼 pattern)
export function parseJobPostings(response: string): JobPostingsResponse | null {
  if (!response.includes('**💼 Job Openings') && !response.includes('💼 Job Openings')) {
    return null;
  }

  const titleMatch = response.match(/\*\*💼\s+Job Openings for\s+(.*?)[:\*]/i);
  const title = titleMatch ? `Job Openings for ${titleMatch[1]}` : 'Job Openings';

  const jobs: JobPosting[] = [];
  
  // Pattern: 1. **Job Title** at Company Name — Location — [Apply](url)
  const jobPattern = /\d+\.\s+\*\*(.*?)\*\*\s+at\s+(.*?)\s+—\s+(.*?)(?:\s+—\s+\[Apply\]\((.*?)\))?(?:\n|$)/g;
  let match;
  
  while ((match = jobPattern.exec(response)) !== null) {
    jobs.push({
      title: match[1].trim(),
      company: match[2].trim(),
      location: match[3].trim(),
      applyUrl: match[4] ? match[4].trim() : undefined
    });
  }

  if (jobs.length === 0) {
    return null;
  }

  return { title, jobs };
}

// Parse Business Listings (🏢 pattern)
export function parseBusinessListings(response: string): BusinessListingsResponse | null {
  if (!response.includes('**🏢 Local Businesses') && !response.includes('🏢 Local Businesses')) {
    return null;
  }

  const titleMatch = response.match(/\*\*🏢\s+(Local Businesses.*?)[:\*]/i);
  const title = titleMatch ? titleMatch[1] : 'Local Businesses';

  const businesses: BusinessListing[] = [];
  
  // Split by business blocks (numbered items)
  const businessBlocks = response.split(/\n(?=\d+\.\s+\*\*)/);
  
  for (const block of businessBlocks) {
    if (!/^\d+\./.test(block.trim())) continue;
    
    const nameMatch = block.match(/\d+\.\s+\*\*(.*?)\*\*\s+—\s+(.*?)(?:\s+\(([^)]+)\))?/);
    const phoneMatch = block.match(/📞\s+([\d\s()-]+)/);
    const mapMatch = block.match(/\[View on Map\]\((.*?)\)/);
    const naicsMatch = block.match(/🏢\s+NAICS\s+(\d+)/);
    
    if (nameMatch) {
      businesses.push({
        name: nameMatch[1].trim(),
        location: nameMatch[2].trim(),
        industry: nameMatch[3] ? nameMatch[3].trim() : undefined,
        phone: phoneMatch ? phoneMatch[1].trim() : undefined,
        mapUrl: mapMatch ? mapMatch[1].trim() : undefined,
        naics: naicsMatch ? naicsMatch[1].trim() : undefined
      });
    }
  }

  if (businesses.length === 0) {
    return null;
  }

  return { title, businesses };
}

// Parse Wage Comparison (💰 pattern)
export function parseWageComparison(response: string): WageComparisonResponse | null {
  if (!response.includes('**💰 Wage & Salary Comparison') && !response.includes('💰 Wage & Salary Comparison')) {
    return null;
  }

  const titleMatch = response.match(/\*\*💰\s+(Wage & Salary Comparison.*?)[:\*]/i);
  const title = titleMatch ? titleMatch[1] : 'Wage & Salary Comparison';
  
  const subtitleMatch = response.match(/\*Showing.*?\*/);
  const subtitle = subtitleMatch ? subtitleMatch[0].replace(/\*/g, '') : undefined;

  const occupationMatch = response.match(/\*\*(.*?)\*\*\s+\(SOC\s+([\d-]+)\)/);
  const occupation = occupationMatch ? occupationMatch[1] : 'Occupation';
  const socCode = occupationMatch ? occupationMatch[2] : undefined;

  const sumnerMatch = response.match(/Sumner County:\s+\$([0-9,]+)/);
  const stateMatch = response.match(/Tennessee.*?:\s+\$([0-9,]+)/);
  const nationalMatch = response.match(/National Average:\s+\$([0-9,]+)/);

  const sumnerWage = sumnerMatch ? parseInt(sumnerMatch[1].replace(/,/g, '')) : undefined;
  const stateWage = stateMatch ? parseInt(stateMatch[1].replace(/,/g, '')) : undefined;
  const nationalWage = nationalMatch ? parseInt(nationalMatch[1].replace(/,/g, '')) : undefined;

  // Parse insights (bullet points)
  const insights: string[] = [];
  const insightPattern = /•\s+(.*?)(?=\n|$)/g;
  let insightMatch;
  
  while ((insightMatch = insightPattern.exec(response)) !== null) {
    insights.push(insightMatch[1].trim());
  }

  const noteMatch = response.match(/\*Note:(.*?)(?:\n\n|$)/);
  const note = noteMatch ? noteMatch[1].trim() : undefined;

  return {
    title,
    subtitle,
    occupation,
    socCode,
    sumnerWage,
    stateWage,
    nationalWage,
    insights,
    note
  };
}

// Parse Career Path Steps (🎓 Step pattern)
export function parseCareerPathSteps(response: string): CareerPathStep[] | null {
  if (!response.includes('**Step')) {
    return null;
  }

  const steps: CareerPathStep[] = [];
  
  // Pattern: **Step N: Title**
  const stepPattern = /\*\*Step\s+(\d+):\s+(.*?)\*\*([\s\S]*?)(?=\*\*Step|\*\*💡|$)/g;
  let match;
  
  while ((match = stepPattern.exec(response)) !== null) {
    const stepNumber = parseInt(match[1]);
    const title = match[2].trim();
    const content = match[3];
    
    const educationMatch = content.match(/(?:Education|Requirements):\s+(.*?)(?:\n|$)/);
    const requirementsMatch = content.match(/Requirements:\s+(.*?)(?:\n|$)/);
    const salaryMatch = content.match(/Salary:\s+(.*?)(?:\n|$)/);
    const timeMatch = content.match(/Time:\s+(.*?)(?:\n|$)/);
    
    steps.push({
      stepNumber,
      title,
      education: educationMatch ? educationMatch[1].trim() : undefined,
      requirements: requirementsMatch ? requirementsMatch[1].trim() : undefined,
      salary: salaryMatch ? salaryMatch[1].trim() : undefined,
      time: timeMatch ? timeMatch[1].trim() : undefined
    });
  }

  if (steps.length === 0) {
    return null;
  }

  return steps;
}

// Extract tip text (💡 pattern)
export function extractTipText(response: string): string | null {
  const tipMatch = response.match(/\*💡\s+Tip:(.*?)(?:\n\n|$)/);
  return tipMatch ? tipMatch[1].trim() : null;
}
